<template>
  <JSelect
    v-model="familyMember"
    :error="error"
    :required="required"
    :options="familyMembers"
    :loadMoreLoading="isFetching"
    optionKey="key"
    optionValue="value"
    emitValue="key"
    :disabled="!enabled || !familyMembers.length"
    :label="$t('profile.accountName.label')"
    :placeholder="$t('profile.accountName.placeholder')"
    disableFilter
    @load-more="onLoadMore"
  />
</template>

<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import { JSelect } from '@journey/ui-kit'
import type { Emits, Props } from './FamilyMembersSelectInput.types'
import { useFamilyMembersQuery } from '@/modules/Profile/requests/queries/useFamilyMembersQuery'
import type { FamilyMember } from '@/modules/Profile/types/model/familyMember'
import useAuthStore from '@/modules/Auth/store'
import { FIRST_PAGE } from '@/modules/Profile/constants'

const props = withDefaults(defineProps<Props>(), {
  defaultOptions: () => [],
  isEdit: false,
})

// V-MODEL
const emit = defineEmits<Emits>()
const familyMember = useVModel(props, 'modelValue', emit)

const page = ref(FIRST_PAGE)
const size = ref(20)
const query = ref('')

// FAMILY MEMBERS
const authStore = useAuthStore()
const enabled = computed(() => !props.isEdit)
const { data, isLoading, isFetching } = useFamilyMembersQuery(
  query,
  page,
  size,
  {
    enabled,
    cacheTime: 0,
  }
)

// DATA
const DEFAULT_USER_DATA = {
  key: -1,
  value: `${authStore.user.name.first}.${authStore.user.name.last}`,
}

const familyMembers = ref([DEFAULT_USER_DATA])

function mapOptions(items: FamilyMember[]) {
  return items?.map((item) => ({
    key: item.familyNumber,
    value: `${item.firstName}.${item.lastName}`,
    ...item,
  }))
}

watch(
  data,
  (value) => {
    if (!value) {
      return
    }

    familyMembers.value.push(...mapOptions(value.data.items))
  },
  {
    immediate: true,
  }
)

// LOAD MORE
function onLoadMore() {
  if (isLoading.value) {
    return
  }

  if (page.value === data.value?.data.page.total) {
    return
  }

  page.value += 1
}
</script>
